import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Target, Plus, Trash, CheckCircle } from "@phosphor-icons/react";

const CATEGORIES = ["strength", "speed", "weight", "endurance", "skill"];

export default function Goals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_value: 0, unit: "", category: "strength", target_date: "" });

  const load = async () => {
    const { data } = await api.get("/goals");
    setGoals(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/goals", { ...form, target_value: Number(form.target_value) });
      setOpen(false);
      setForm({ title: "", description: "", target_value: 0, unit: "", category: "strength", target_date: "" });
      load();
      toast.success("Goal added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateProgress = async (g, current) => {
    try {
      await api.put(`/goals/${g.id}`, { current_value: Number(current) });
      load();
    } catch { toast.error("Update failed"); }
  };

  const complete = async (g) => {
    await api.put(`/goals/${g.id}`, { status: "completed" });
    load();
    toast.success("Goal completed 💪");
  };

  const del = async (g) => {
    await api.delete(`/goals/${g.id}`);
    load();
  };

  const isFree = user?.subscription_tier === "free";
  const atLimit = isFree && goals.length >= 1;

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Targets</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <Target size={48} weight="duotone" className="text-[#FF3B30]" /> Goals
            </h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button disabled={atLimit} data-testid="new-goal"
                className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6 disabled:opacity-50">
                <Plus size={18} weight="bold" className="mr-2" /> New Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121212] border-[#262626] rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading font-black uppercase text-2xl">New Goal</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="font-heading uppercase tracking-widest text-xs">Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="goal-title" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Target</Label>
                    <Input type="number" required value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })}
                      data-testid="goal-target" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Unit</Label>
                    <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      placeholder="kg, s, reps" data-testid="goal-unit" className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="font-heading uppercase tracking-widest text-xs">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-2 rounded-none bg-[#0A0A0A] border-[#262626]" data-testid="goal-category"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" data-testid="goal-submit"
                  className="w-full rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold">
                  Create Goal
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {atLimit && (
          <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-4 mb-6 text-sm" data-testid="goal-limit-notice">
            Free plan is limited to 1 goal. <Link to="/pricing" className="underline font-heading uppercase tracking-widest text-xs">Upgrade for unlimited</Link>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          {goals.map((g) => {
            const pct = Math.min(100, g.target_value ? (g.current_value / g.target_value) * 100 : 0);
            return (
              <div key={g.id} className="border border-[#262626] bg-[#121212] p-6" data-testid={`goal-${g.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#FF3B30]">{g.category}</div>
                    <div className="font-heading font-bold uppercase text-xl mt-1">{g.title}</div>
                  </div>
                  <div className="flex gap-2">
                    {g.status !== "completed" && (
                      <button onClick={() => complete(g)} className="text-[#34C759] hover:opacity-80" data-testid={`complete-${g.id}`}>
                        <CheckCircle size={20} weight="bold" />
                      </button>
                    )}
                    <button onClick={() => del(g)} className="text-[#A3A3A3] hover:text-[#FF3B30]" data-testid={`delete-${g.id}`}>
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-heading font-black text-3xl">{g.current_value}</span>
                  <span className="text-[#A3A3A3]">/ {g.target_value} {g.unit}</span>
                </div>
                <div className="h-2 bg-[#262626] mb-3">
                  <div className="h-full bg-[#FF3B30] transition-all" style={{ width: `${pct}%` }} />
                </div>
                {g.status !== "completed" && (
                  <div className="flex gap-2">
                    <Input type="number" defaultValue={g.current_value}
                      onBlur={(e) => updateProgress(g, e.target.value)}
                      data-testid={`update-${g.id}`}
                      className="rounded-none bg-[#0A0A0A] border-[#262626] h-9 text-sm" />
                  </div>
                )}
                {g.status === "completed" && (
                  <div className="text-xs font-heading uppercase tracking-widest text-[#34C759]">✓ Completed</div>
                )}
              </div>
            );
          })}
        </div>

        {goals.length === 0 && (
          <div className="border-2 border-dashed border-[#262626] p-16 text-center" data-testid="empty-goals">
            <Target size={48} className="text-[#A3A3A3] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-widest text-[#A3A3A3]">No goals yet</div>
            <div className="text-sm text-[#A3A3A3] mt-2">Set a target for the level above yours.</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
